import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CalendarPicker} from '@mui/x-date-pickers';
import {Event as CalendarIcon} from '@mui/icons-material';
import dayjs from 'dayjs';
import {registerGlobalStyle} from '../../theme';
import {HoopsPropTypes} from '../utils';
import {AutoCompleteContainer, AutoCompletePopover, AutoCompleteValueContainer} from '../Popovers';
import {TextInput} from '../Basic';
import {formatDateShort} from '../../utils';
import {formatInternationalDate} from '../../utils/internationalDate';

registerGlobalStyle('.date-picker', (theme) => ({
  '*': {cursor: 'pointer',},
  '.popover-open .text-input': {borderColor: theme.colors.border.focus},
  '.suffix': {
    color: theme.colors.text.mediumIcon,
    transition: theme.transitions.out.color,
    marginRight: theme.spacing(1),
  },
  '.popover-open .suffix': {
    color: theme.colors.text.highlight,
    transition: theme.transitions.in.color,
  },
  '.clearable-icon': {right: theme.spacing(2)},
}));

const minDate = new Date((new Date()).getFullYear() - 2, 0, 0, 0, 0, 0);

export function DatePicker({className, noClear, initialOpen, label, placeholder, placement, value, onChange, onValueChange, onClose}) {
  const [view, setView] = useState('day');
  const [date, setDate] = useState(value ? dayjs(value) : null);
  const contextRef = useRef();

  useEffect(() => {
    setDate(value ? dayjs(value) : null);
  }, [value]);

  const handleChange = useCallback((newDate) => {
    if (view === 'day') {
      if (contextRef.current) {
        contextRef.current.closePopover();
      }

      // Convert the Dayjs date to a string, that will be consistent across time zones
      const dateStr = formatInternationalDate(newDate);
      onChange?.(dateStr);
      onValueChange?.(dateStr);
    } else {
      setView('day');
    }
  }, [view, onChange, onValueChange]);

  const handleClearDate = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange?.(null);
    onValueChange?.(null);
  }, [onChange, onValueChange]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.clearable-icon')) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, [setView]);

  return (
    <AutoCompleteContainer
      className={[className, 'date-picker']}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onClose={onClose}
      contextRef={contextRef}
    >
      <AutoCompleteValueContainer initialOpen={initialOpen}>
        <TextInput
          readOnly
          value={formatDateShort(date)}
          clearable={value && !noClear}
          clearableReadOnly
          type={'tel'}
          suffix={CalendarIcon}
          onClear={handleClearDate}
          onMouseDown={handleMouseDown}
        />
      </AutoCompleteValueContainer>
      <AutoCompletePopover className={'date-picker-popover'} placement={placement}>
        <CalendarPicker date={date} minDate={minDate} onChange={handleChange} onViewChange={handleViewChange} view={view}/>
      </AutoCompletePopover>
    </AutoCompleteContainer>
  );
}

DatePicker.propTypes = {
  className: HoopsPropTypes.className,
  initialOpen: HoopsPropTypes.bool,
  label: HoopsPropTypes.string,
  placeholder: HoopsPropTypes.string,
  placement: HoopsPropTypes.string,
  value: HoopsPropTypes.oneOfType([HoopsPropTypes.string, HoopsPropTypes.object]),
  onChange: HoopsPropTypes.func,
  onClose: HoopsPropTypes.func,
  onValueChange: HoopsPropTypes.func,
};
